import { z } from "zod";

/** Time curves are independent of the route drawn through space. */
export const zMotionEasing = z.enum(["linear", "ease-in", "ease-out", "ease-in-out"]);
export type MotionEasing = z.infer<typeof zMotionEasing>;

/** Fractions of the location's coordinate frame, independent of puppet size. */
export const zStageMark = z.union([
  z.number().min(1).max(5).multipleOf(0.5),
  z
    .object({
      x: z.number().finite().min(0).max(1),
      y: z.number().finite().min(0).max(1.2).optional(),
    })
    .strict(),
]);
export type StageMark = z.infer<typeof zStageMark>;
export const zStagePosition = z
  .object({
    location: z.string().min(1).optional(),
    slot: z.number().min(1).max(5).multipleOf(0.5).optional(),
    mark: zStageMark.optional(),
    nudge: z.number().finite().optional(),
  })
  .strict();
export type StagePosition = z.infer<typeof zStagePosition>;
export const stageMoveFields = {
  /** `walk` is a slide taken in hop-steps, the puppet rocking side to side. */
  moveType: z.enum(["slide", "walk", "pop", "path", "jump", "fly"]).optional(),
  flight: z.enum(["cruise", "takeoff", "land", "hover"]).optional(),
  /** Arc height in puppet frame heights; defaults are chosen by the preset. */
  arcHeight: z.number().finite().min(0).max(3).optional(),
  /** Maximum bank in degrees. Zero keeps the puppet upright. */
  bank: z.number().finite().min(0).max(60).optional(),
  travelMs: z.number().finite().min(0).max(60000).optional(),
  from: zStagePosition.optional(),
  via: z.array(zStagePosition).max(24).optional(),
};
export type StageMoveStyle = {
  moveType?: "slide" | "walk" | "pop" | "path" | "jump" | "fly";
  flight?: "cruise" | "takeoff" | "land" | "hover";
  arcHeight?: number;
  bank?: number;
};
/** Offsets in artwork fractions (positive X right, positive Y down). */
export const zEffectOffset = z.object({
  x: z.number().finite().min(-1).max(1),
  y: z.number().finite().min(-1).max(1),
}).strict();
export const zLocationSpan = z
  .object({
    start: z.number().finite().min(0).max(1),
    end: z.number().finite().min(0).max(1),
  })
  .strict()
  .refine((v) => v.start < v.end, "Row start must be before its end.");
